/*
  "Add code" -- redeem a credit code from any page (owner request, 3 Oct 2026).

  One dialog for the whole site (snippets/add-code-dialog.liquid, rendered once by the header).
  Anything carrying [data-ttv-add-code] opens it: the header button, the phone menu button,
  "Have a code?" on the Credits and membership page and on the generator page.

  Signed in: the code is sent once, in the request body, to the same Orders call the generator
  page always used -- POST /apps/generator/credits/redeem {code} -- and its answer is read the
  same way: 200 { added, balance, unlimited, ... }, 401 for a guest, 400 { message } for a code
  the ledger refuses ("that code is not valid", "that code has expired", "you have already used
  that code", used up). The new balance reaches the header chip (`ttv:credits`), any
  self-loading credit meter, and the generator page's own account state (`ttv:code-redeemed`)
  without a reload.

  Signed out: the same dialog takes the code, keeps it in localStorage (ttv-pending-code) and
  goes to account creation, coming back to this page; once signed in, the dialog opens with the
  code filled in and the stored copy is cleared. Every storage call is wrapped in try/catch.

  Code links: ?code=XYZ on any page (trimmed, uppercased, only [A-Z0-9-]{3,40}) opens the dialog
  filled in; the parameter is removed from the address straight away (history.replaceState).

  The pure helpers are exported (window.TTVAddCodeLogic, or module.exports under Node) for
  tests/add-code.test.js.
*/
(function (root) {
  'use strict';

  var PENDING_KEY = 'ttv-pending-code';
  var PENDING_TTL = 24 * 60 * 60 * 1000;
  var CODE_RE = /^[A-Z0-9-]{3,40}$/;
  var NETWORK_TEXT = 'Could not check your code just now. Please try again in a moment.';
  var RETRY_TEXT = 'That did not work just now. Please try again in a moment.';

  // A code link's value: trimmed, uppercased, and only the shape a real code has.
  function cleanCode(raw) {
    var c = String(raw == null ? '' : raw).trim().toUpperCase();
    return CODE_RE.test(c) ? c : '';
  }

  // What the teacher typed: trimmed and uppercased (the ledger uppercases too), nothing more --
  // the ledger is the judge of whether it is a real code.
  function typedCode(raw) {
    return String(raw == null ? '' : raw).trim().toUpperCase().slice(0, 40);
  }

  function sentence(text) {
    var t = String(text || '').trim();
    if (!t) return '';
    t = t.charAt(0).toUpperCase() + t.slice(1);
    return /[.!?]$/.test(t) ? t : t + '.';
  }

  function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }

  // Every Orders response carries the account; /credits/redeem returns it flat.
  function creditsOf(body) {
    if (!body || typeof body !== 'object') return null;
    var c = body.credits && typeof body.credits === 'object' ? body.credits : body;
    if (typeof c.unlimited === 'undefined' && typeof c.balance === 'undefined') return null;
    return { balance: typeof c.balance === 'number' ? c.balance : null, unlimited: !!c.unlimited };
  }

  function successText(body) {
    var added = parseInt(body && body.added, 10);
    var c = creditsOf(body);
    var now = '';
    if (c && c.unlimited) now = 'unlimited credits';
    else if (c && c.balance !== null) now = plural(Math.max(0, c.balance), 'credit', 'credits');
    return plural(isNaN(added) ? 0 : added, 'credit', 'credits') + ' added.' + (now ? ' You now have ' + now + '.' : '');
  }

  // The generator page's own reading of a refusal, unchanged: the ledger's sentence on a 400.
  function errorText(status, body) {
    if (status === 401) return 'Sign in to use a code.';
    if (status === 400 && body && body.message) return sentence(body.message);
    return RETRY_TEXT;
  }

  function parseJson(text) {
    try { return text ? JSON.parse(text) : null; } catch (e) { return null; }
  }

  // The code goes in the request body once and nowhere else: never an address, never the console.
  function redeem(code, opts) {
    opts = opts || {};
    var fetchFn = opts.fetch || root.fetch;
    var apiBase = String(opts.apiBase || '/apps/generator').replace(/\/+$/, '');
    return Promise.resolve().then(function () {
      return fetchFn(apiBase + '/credits/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ code: code })
      });
    }).then(function (res) {
      return res.text().then(parseJson, function () { return null; }).then(function (body) {
        if (res.ok) return { ok: true, status: res.status, body: body, text: successText(body), credits: creditsOf(body) };
        if (!(res.status === 401 || res.status === 400) && root.console && root.console.warn) {
          root.console.warn('[add-code] /credits/redeem failed', res.status);
        }
        return { ok: false, status: res.status, body: body, text: errorText(res.status, body) };
      });
    }).catch(function () {
      return { ok: false, status: 0, body: null, text: NETWORK_TEXT };
    });
  }

  // Reads ?code= from an address. `found` says the parameter was there at all (so it is removed
  // even when its value is unusable); `url` is the same address without it.
  function codeFromUrl(href) {
    var u;
    try { u = new URL(href); } catch (e) { return { found: false, code: '', url: '' }; }
    if (!u.searchParams.has('code')) return { found: false, code: '', url: u.pathname + u.search + u.hash };
    var code = cleanCode(u.searchParams.get('code'));
    u.searchParams.delete('code');
    return { found: true, code: code, url: u.pathname + u.search + u.hash };
  }

  // The register/login route with its return_url pointed at `back` (same rewrite as
  // snippets/start-free.liquid; the route already carries its own query, see account-return-link).
  function returnUrlFor(baseHref, back) {
    // New customer accounts: return_to on /customer_authentication/login (assets/sign-in-return.js).
    var SR = root.TTVSignInReturn || (typeof module === 'object' && module.exports ? require('./sign-in-return.js') : null);
    if (SR) return SR.withReturn(baseHref || '', back || '/');
    var href = String(baseHref || '');
    var enc = encodeURIComponent(back || '/');
    if (/[?&]return_url=/.test(href)) return href.replace(/([?&])return_url=[^&]*/, '$1return_url=' + enc);
    return href + (href.indexOf('?') === -1 ? '?' : '&') + 'return_url=' + enc;
  }

  function storage() {
    try { return root.localStorage || null; } catch (e) { return null; }
  }
  function savePending(code, now) {
    var s = storage();
    if (!s || !code) return false;
    try { s.setItem(PENDING_KEY, JSON.stringify({ code: code, at: now || Date.now() })); return true; } catch (e) { return false; }
  }
  function readPending(now) {
    var s = storage();
    if (!s) return '';
    try {
      var r = JSON.parse(s.getItem(PENDING_KEY) || 'null');
      if (!r || typeof r.at !== 'number' || (now || Date.now()) - r.at > PENDING_TTL) return '';
      return cleanCode(r.code);
    } catch (e) { return ''; }
  }
  function clearPending() {
    var s = storage();
    if (!s) return;
    try { s.removeItem(PENDING_KEY); } catch (e) { /* nothing stored */ }
  }

  var logic = {
    PENDING_KEY: PENDING_KEY, cleanCode: cleanCode, typedCode: typedCode, sentence: sentence,
    creditsOf: creditsOf, successText: successText, errorText: errorText, redeem: redeem,
    codeFromUrl: codeFromUrl, returnUrlFor: returnUrlFor,
    savePending: savePending, readPending: readPending, clearPending: clearPending
  };
  root.TTVAddCodeLogic = logic;
  if (typeof module !== 'undefined' && module.exports) module.exports = logic;

  // ---- The dialog ----
  var doc = root.document;
  // (Not `window.TTVAddCode`: the dialog's own id makes that name a global pointing at the element.)
  if (!doc || root.TTVCodeDialog) return;

  function start() {
    var dialog = doc.querySelector('[data-ttv-add-code-dialog]');
    if (!dialog) return;
    var signedIn = dialog.getAttribute('data-signed-in') === 'true';
    var apiBase = dialog.getAttribute('data-api-base') || '/apps/generator';
    var form = dialog.querySelector('[data-ttv-add-code-form]');
    var input = dialog.querySelector('[data-ttv-add-code-input]');
    var msg = dialog.querySelector('[data-ttv-add-code-msg]');
    var apply = dialog.querySelector('[data-ttv-add-code-apply]');
    var signinLink = dialog.querySelector('[data-ttv-add-code-signin]');
    var applyLabel = apply ? apply.textContent : 'Apply';
    var opener = null;
    var done = false;
    var busy = false;

    // kind: 'error', 'success', or nothing for a plain progress line ("Checking...").
    function say(text, kind) {
      var isError = kind === 'error';
      if (!msg) return;
      msg.textContent = text || '';
      msg.hidden = !text;
      msg.classList.toggle('is-error', isError);
      msg.classList.toggle('is-success', !!text && kind === 'success');
      if (input) {
        if (isError) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      }
    }

    function setDone(on) {
      done = !!on;
      if (apply) apply.textContent = done ? 'Done' : applyLabel;
    }

    function isOpen() { return dialog.hasAttribute('open'); }

    function open(code, from) {
      opener = from || (doc.activeElement !== doc.body ? doc.activeElement : null);
      say('');
      setDone(false);
      if (input) input.value = code || '';
      if (!isOpen()) {
        if (typeof dialog.showModal === 'function') {
          try { dialog.showModal(); } catch (e) { dialog.setAttribute('open', ''); }
        } else {
          dialog.setAttribute('open', '');
        }
      }
      doc.documentElement.classList.add('ttv-add-code-open');
      if (input) { input.focus(); try { input.setSelectionRange(input.value.length, input.value.length); } catch (e) { /* fine */ } }
    }

    function close() {
      if (typeof dialog.close === 'function' && isOpen()) {
        try { dialog.close(); } catch (e) { dialog.removeAttribute('open'); }
      } else {
        dialog.removeAttribute('open');
      }
    }

    dialog.addEventListener('close', function () {
      doc.documentElement.classList.remove('ttv-add-code-open');
      if (opener && typeof opener.focus === 'function' && doc.contains(opener)) {
        try { opener.focus(); } catch (e) { /* the page keeps its focus */ }
      }
      opener = null;
    });

    function focusables() {
      return Array.prototype.filter.call(
        dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'),
        function (el) { return !el.hidden && el.offsetParent !== null; }
      );
    }

    // Esc closes (the browser's own `cancel` does it for a modal; this covers the fallback too),
    // and Tab stays inside the dialog.
    dialog.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' || ev.key === 'Esc') { ev.preventDefault(); close(); return; }
      if (ev.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (ev.shiftKey && doc.activeElement === first) { ev.preventDefault(); last.focus(); }
      else if (!ev.shiftKey && doc.activeElement === last) { ev.preventDefault(); first.focus(); }
    });

    // A click on the dim backdrop (the dialog element itself, outside its panel) closes it.
    dialog.addEventListener('click', function (ev) {
      if (ev.target === dialog) close();
    });
    Array.prototype.forEach.call(dialog.querySelectorAll('[data-ttv-add-code-close]'), function (b) {
      b.addEventListener('click', function () { close(); });
    });

    function hereWithoutCode() {
      var r = codeFromUrl(root.location.href);
      return r.url || '/';
    }

    function announce(result) {
      var c = result.credits;
      try { doc.dispatchEvent(new CustomEvent('ttv:code-redeemed', { detail: result.body })); } catch (e) { /* the page catches up on reload */ }
      if (c) {
        try { doc.dispatchEvent(new CustomEvent('ttv:credits', { detail: { balance: c.balance, unlimited: c.unlimited } })); } catch (e) { /* the chip catches up on the next page */ }
      }
      // A meter that loads itself (Credits and membership page) takes the new balance directly;
      // the generator page's meter follows its own state through `ttv:code-redeemed`.
      if (root.TVCreditMeter && typeof root.TVCreditMeter.apply === 'function' && result.body) {
        Array.prototype.forEach.call(doc.querySelectorAll('[data-credit-meter][data-credit-meter-auto]'), function (m) {
          root.TVCreditMeter.apply(m, result.body);
        });
      }
    }

    function submit() {
      if (done) { close(); return; }
      if (busy) return;
      var code = typedCode(input ? input.value : '');
      if (!code) { say('Type your code first.', 'error'); if (input) input.focus(); return; }

      if (!signedIn) {
        savePending(code);
        root.location.assign(returnUrlFor(dialog.getAttribute('data-register-url'), hereWithoutCode()));
        return;
      }

      busy = true;
      if (apply) apply.disabled = true;
      say('Checking…');
      redeem(code, { apiBase: apiBase }).then(function (result) {
        if (result.ok) {
          if (input) input.value = '';
          clearPending();
          say(result.text, 'success');
          setDone(true);
          announce(result);
        } else {
          say(result.text, 'error');
        }
      }).then(function () {
        busy = false;
        if (apply) { apply.disabled = false; if (done) apply.focus(); }
      });
    }

    if (form) {
      form.addEventListener('submit', function (ev) { ev.preventDefault(); submit(); });
    }

    // A guest who already has an account signs in instead; the code they typed rides along the same way.
    if (signinLink) {
      signinLink.addEventListener('click', function () {
        var code = typedCode(input ? input.value : '');
        if (code) savePending(code);
        signinLink.setAttribute('href', returnUrlFor(signinLink.getAttribute('href'), hereWithoutCode()));
      });
    }

    // Every opener on the page, including ones added later (event delegation).
    doc.addEventListener('click', function (ev) {
      var t = ev.target && ev.target.closest ? ev.target.closest('[data-ttv-add-code]') : null;
      if (!t) return;
      ev.preventDefault();
      open('', t);
    });

    root.TTVCodeDialog = { open: function (code) { open(cleanCode(code) || typedCode(code)); }, close: close };

    // A code link, or a code a guest left before creating their account.
    var fromUrl = codeFromUrl(root.location.href);
    if (fromUrl.found) {
      try { root.history.replaceState(root.history.state, '', fromUrl.url); } catch (e) { /* the address keeps it; nothing else does */ }
    }
    if (fromUrl.code) {
      if (signedIn) clearPending();
      else savePending(fromUrl.code);
      open(fromUrl.code);
      return;
    }
    if (signedIn) {
      var pending = readPending();
      if (pending) { clearPending(); open(pending); }
    }
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start);
  else start();
})(typeof window !== 'undefined' ? window : globalThis);
